import i18n from 'i18n';

export interface QuerySyntaxIssue {
    message: string;
    start: number;
    end: number;
}

export interface QuerySyntaxValidation {
    issues: QuerySyntaxIssue[];
}

interface QuotedPhrase {
    start: number;
    end: number;
    text: string;
    isClosed: boolean;
}

const isEscaped = (value: string, index: number): boolean => {
    let backslashes = 0;

    for (let i = index - 1; i >= 0 && value[i] === '\\'; i--) {
        backslashes++;
    }

    return backslashes % 2 === 1;
};

const isInsideQuotes = (value: string, index: number): boolean => {
    let inQuotes = false;

    for (let i = 0; i < index; i++) {
        if (value[i] === '"' && !isEscaped(value, i)) {
            inQuotes = !inQuotes;
        }
    }

    return inQuotes;
};

const findQuotedPhrases = (value: string): QuotedPhrase[] => {
    const phrases: QuotedPhrase[] = [];
    let i = 0;

    while (i < value.length) {
        if (value[i] !== '"' || isEscaped(value, i)) {
            i++;
            continue;
        }

        const start = i;
        i++;

        let closingQuoteIndex = -1;

        while (i < value.length) {
            if (value[i] === '"' && !isEscaped(value, i)) {
                closingQuoteIndex = i;
                i++;
                break;
            }

            i++;
        }

        if (closingQuoteIndex >= 0) {
            phrases.push({
                start,
                end: closingQuoteIndex + 1,
                text: value.slice(start, closingQuoteIndex + 1),
                isClosed: true,
            });
            continue;
        }

        phrases.push({
            start,
            end: value.length,
            text: value.slice(start),
            isClosed: false,
        });
        break;
    }

    return phrases;
};

export const validateLuceneQuerySyntax = (value: string): QuerySyntaxValidation => {
    const issues: QuerySyntaxIssue[] = [];

    if (!value.trim()) {
        return {issues};
    }

    const quotedPhrases = findQuotedPhrases(value);

    for (const phrase of quotedPhrases) {
        if (!phrase.isClosed) {
            issues.push({
                message: i18n.t('chart_settings_query_unclosed_quote.text'),
                start: phrase.start,
                end: phrase.end,
            });
            break;
        }
    }

    let depth = 0;
    let unmatchedOpenIndex = -1;

    for (let i = 0; i < value.length; i++) {
        if (isInsideQuotes(value, i)) {
            continue;
        }

        if (value[i] === '(') {
            if (depth === 0) {
                unmatchedOpenIndex = i;
            }
            depth++;
            continue;
        }

        if (value[i] !== ')') {
            continue;
        }

        depth--;

        if (depth < 0) {
            issues.push({
                message: i18n.t('chart_settings_query_unbalanced_parenthesis.text'),
                start: i,
                end: i + 1,
            });
            break;
        }
    }

    if (depth > 0 && unmatchedOpenIndex >= 0) {
        issues.push({
            message: i18n.t('chart_settings_query_unbalanced_parenthesis.text'),
            start: unmatchedOpenIndex,
            end: unmatchedOpenIndex + 1,
        });
    }

    const operatorPattern = /\b(and|or|not)\b/gi;
    let operatorMatch: RegExpExecArray | null;

    while ((operatorMatch = operatorPattern.exec(value)) !== null) {
        const start = operatorMatch.index;
        const end = start + operatorMatch[0].length;

        if (isInsideQuotes(value, start)) {
            continue;
        }

        if (operatorMatch[0] === operatorMatch[0].toUpperCase()) {
            continue;
        }

        issues.push({
            message: i18n.t('hp_lowercase.text'),
            start,
            end,
        });
    }

    const tildePattern = /~\s+\d+/g;
    let tildeMatch: RegExpExecArray | null;

    while ((tildeMatch = tildePattern.exec(value)) !== null) {
        issues.push({
            message: i18n.t('hp_number.text'),
            start: tildeMatch.index,
            end: tildeMatch.index + tildeMatch[0].length,
        });
    }

    for (const phrase of quotedPhrases) {
        if (!phrase.isClosed) {
            continue;
        }

        const inner = phrase.text.slice(1, -1);
        const wildcardIndex = inner.indexOf('*');

        if (wildcardIndex >= 0) {
            issues.push({
                message: i18n.t('hp_wildcard.text'),
                start: phrase.start + 1 + wildcardIndex,
                end: phrase.start + 1 + wildcardIndex + 1,
            });
        }

        const orInQuote = /\bor\b/i.exec(inner);

        if (orInQuote?.index !== undefined) {
            issues.push({
                message: i18n.t('hp_or_search.text'),
                start: phrase.start + 1 + orInQuote.index,
                end: phrase.start + 1 + orInQuote.index + orInQuote[0].length,
            });
        }

        const andInQuote = /\band\b/i.exec(inner);

        if (andInQuote?.index !== undefined) {
            issues.push({
                message: i18n.t('hp_and_search.text'),
                start: phrase.start + 1 + andInQuote.index,
                end: phrase.start + 1 + andInQuote.index + andInQuote[0].length,
            });
        }
    }

    return {issues};
};

export const getPrimaryQuerySyntaxIssue = (
    validation: QuerySyntaxValidation,
): QuerySyntaxIssue | undefined => validation.issues[0];
